/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

/**
 * GraphQL query builder for Enhanced PDP Content
 * Returns the complete GraphQL query string with all fragments and variables
 */

export type EnhancedPdpQueryVariables = {
  locale: string;
  brandname: string;
  skuid: string[];
  taxonomy: string;
};

/**
 * Builds the complete Enhanced PDP Content GraphQL query
 * Includes 4 specificity levels and all 29 modular block types
 */
export function buildEnhancedPdpContentQuery(_variables: EnhancedPdpQueryVariables): string {
  return `
# =====================================================================
# ENHANCED PDP CONTENT QUERY - MULTI-SPECIFICITY FALLBACK
# Complete GraphQL Query for enhanced_pdp_content with 4 specificity levels
# Generated: 2026-08-26
# =====================================================================

query EnhancedPdpContentQuery(
  $locale: String!
  $brandname: String!
  $skuid: [String!]!
  $taxonomy: String!
) {
  # ===================================================================
  # ALIAS 1: Brand + SKU (Most Specific)
  # Query: WHERE brandname=$brandname AND skuid_in=$skuid
  # ===================================================================
  brandAndSku: all_enhanced_pdp_content(
    locale: $locale
    where: { target_attributes: { brandname: $brandname, skuid_in: $skuid, taxonomy:  {selectedItems: {seoUrl_exists: false} } } }
    limit: 1
    order_by: [updated_at_DESC]
  ) {
    total
    items {
      ...EnhancedPdpContentFields
    }
  }

  # ===================================================================
  # ALIAS 2: Brand + Taxonomy (Medium-High Specificity)
  # Query: WHERE brandname=$brandname AND skuid=[] AND taxonomy=$taxonomyPath
  # ===================================================================
  brandAndTaxonomy: all_enhanced_pdp_content(
    locale: $locale
    where: { target_attributes: { brandname: $brandname, skuid: [], taxonomy: { selectedItems: {seoUrl: $taxonomy} } } }
    limit: 1
    order_by: [updated_at_DESC]
  ) {
    total
    items {
      ...EnhancedPdpContentFields
    }
  }

  # ===================================================================
  # ALIAS 3: Brand Only (Medium-Low Specificity)
  # Query: WHERE brandname=$brandname AND skuid=[] AND taxonomy NOT EXISTS
  # ===================================================================
  brandOnly: all_enhanced_pdp_content(
    locale: $locale
    where: { target_attributes: { brandname: $brandname, skuid: [], taxonomy:  {selectedItems: {seoUrl_exists: false} } } }
    limit: 1
    order_by: [updated_at_DESC]
  ) {
    total
    items {
      ...EnhancedPdpContentFields
    }
  }

  # ===================================================================
  # ALIAS 4: Taxonomy Only (Least Specific)
  # Query: WHERE brandname="" AND skuid=[] AND taxonomy=$taxonomyPath
  # ===================================================================
  taxonomyOnly: all_enhanced_pdp_content(
    locale: $locale
    where: { target_attributes: { brandname: "", skuid: [], taxonomy: { selectedItems: {seoUrl: $taxonomy} } } }
    limit: 1
    order_by: [updated_at_DESC]
  ) {
    total
    items {
      ...EnhancedPdpContentFields
    }
  }
}

# =====================================================================
# MAIN CONTENT FRAGMENT
# =====================================================================
fragment EnhancedPdpContentFields on EnhancedPdpContent {
  system {
    uid
    content_type_uid
    locale
    created_at
    updated_at
  }
  title
  target_attributes {
    brandname
    skuid
    taxonomy
  }
  anchor_block {
    anchor_position
    content {
      __typename

      # 1. Banner
      ... on AnchorBlockContentBanner {
        banner {
          bannerConnection {
            totalCount
            edges {
              node {
                ... on Banner {
                  ...BannerFields
                }
              }
            }
          }
        }
      }

      # 2. Banner Split
      ... on AnchorBlockContentBannerSplit {
        banner_split {
          banner_splitConnection {
            totalCount
            edges {
              node {
                ... on BannerSplit {
                  ...BannerSplitFields
                }
              }
            }
          }
        }
      }

      # 3. Product Chart
      ... on AnchorBlockContentProductChart {
        product_chart {
          product_chartConnection {
            totalCount
            edges {
              node {
                ... on ProductChart {
                  ...ProductChartFields
                }
              }
            }
          }
        }
      }

      # 4. FAQ
      ... on AnchorBlockContentFaq {
        faq {
          faqConnection {
            totalCount
            edges {
              node {
                ... on Faq {
                  ...FaqFields
                }
              }
            }
          }
        }
      }

      # 5. Image + Content
      ... on AnchorBlockContentImageContent {
        image_content {
          image_contentConnection {
            totalCount
            edges {
              node {
                ... on ImageContent {
                  ...ImageContentFields
                }
              }
            }
          }
        }
      }

      # 6. PartType Grid
      ... on AnchorBlockContentParttypeGrid {
        parttype_grid {
          parttype_gridConnection {
            totalCount
            edges {
              node {
                ... on ParttypeGrid {
                  ...ParttypeGridFields
                }
              }
            }
          }
        }
      }

      # 7. Category Grid
      ... on AnchorBlockContentCategoryGrid {
        category_grid {
          category_gridConnection {
            totalCount
            edges {
              node {
                ... on CategoryGrid {
                  ...CategoryGridFields
                }
              }
            }
          }
        }
      }

      # 8. Logo + Description
      ... on AnchorBlockContentLogoDescription {
        logo_description {
          logo_descriptionConnection {
            totalCount
            edges {
              node {
                ... on LogoDescription {
                  ...LogoDescriptionFields
                }
              }
            }
          }
        }
      }

      # 9. Image + Content Background
      ... on AnchorBlockContentImageContentBackground {
        image_content_background {
          image_content_backgroundConnection {
            totalCount
            edges {
              node {
                ... on ImageContentBackground {
                  ...ImageContentBackgroundFields
                }
              }
            }
          }
        }
      }

      # 10. Image + Category Grid
      ... on AnchorBlockContentImageCategoryGrid {
        image_category_grid {
          image_category_gridConnection {
            totalCount
            edges {
              node {
                ... on ImageCategoryGrid {
                  ...ImageCategoryGridFields
                }
              }
            }
          }
        }
      }

      # 11. Menu List
      ... on AnchorBlockContentMenuList {
        menu_list {
          menu_listConnection {
            totalCount
            edges {
              node {
                ... on MenuList {
                  ...MenuListFields
                }
              }
            }
          }
        }
      }

      # 12. Banner + CTA
      ... on AnchorBlockContentBannerCta {
        banner_cta {
          banner_ctaConnection {
            totalCount
            edges {
              node {
                ... on BannerCta {
                  ...BannerCtaFields
                }
              }
            }
          }
        }
      }

      # 13. Product Grid
      ... on AnchorBlockContentProductGrid {
        product_grid {
          product_gridConnection {
            totalCount
            edges {
              node {
                ... on ProductGrid {
                  ...ProductGridFields
                }
              }
            }
          }
        }
      }

      # 14. Rich Text Editor
      ... on AnchorBlockContentRichTextEditor {
        rich_text_editor {
          rich_text_editorConnection {
            totalCount
            edges {
              node {
                ... on RichTextEditor {
                  ...RichTextEditorFields
                }
              }
            }
          }
        }
      }

      # 15. BlogType Grid
      ... on AnchorBlockContentBlogtypeGrid {
        blogtype_grid {
          blogtype_gridConnection {
            totalCount
            edges {
              node {
                ... on BlogtypeGrid {
                  ...BlogtypeGridFields
                }
              }
            }
          }
        }
      }

      # 16. Featured Content
      ... on AnchorBlockContentFeaturedContent {
        featured_content {
          featured_contentConnection {
            totalCount
            edges {
              node {
                ... on FeaturedContent {
                  ...FeaturedContentFields
                }
              }
            }
          }
        }
      }

      # 17. Video + Content
      ... on AnchorBlockContentVideoContent {
        video_content {
          video_contentConnection {
            totalCount
            edges {
              node {
                ... on VideoContent {
                  ...VideoContentFields
                }
              }
            }
          }
        }
      }

      # 18. Button Grid
      ... on AnchorBlockContentButtonGrid {
        button_grid {
          button_gridConnection {
            totalCount
            edges {
              node {
                ... on ButtonGrid {
                  ...ButtonGridFields
                }
              }
            }
          }
        }
      }

      # 19. Image Video Split
      ... on AnchorBlockContentImageVideoSplit {
        image_video_split {
          image_video_splitConnection {
            totalCount
            edges {
              node {
                ... on ImageVideoSplit {
                  ...ImageVideoSplitFields
                }
              }
            }
          }
        }
      }

      # 20. Icon Grid
      ... on AnchorBlockContentIconGrid {
        icon_grid {
          icon_gridConnection {
            totalCount
            edges {
              node {
                ... on IconGrid {
                  ...IconGridFields
                }
              }
            }
          }
        }
      }

      # 21. Super Duper Hero
      ... on AnchorBlockContentSuperDuperHero {
        super_duper_hero {
          super_duper_heroConnection {
            totalCount
            edges {
              node {
                ... on SuperDuperHero {
                  ...SuperDuperHeroFields
                }
              }
            }
          }
        }
      }

      # 22. Certona
      ... on AnchorBlockContentCertona {
        certona {
          certonaConnection {
            totalCount
            edges {
              node {
                ... on Certona {
                  ...CertonaFields
                }
              }
            }
          }
        }
      }

      # 23. Experience Injector
      ... on AnchorBlockContentExperienceInjector {
        experience_injector {
          experience_injectorConnection {
            totalCount
            edges {
              node {
                ... on ExperienceInjector {
                  ...ExperienceInjectorFields
                }
              }
            }
          }
        }
      }

      # 24. Target Div
      ... on AnchorBlockContentTargetDiv {
        target_div {
          target_divConnection {
            totalCount
            edges {
              node {
                ... on TargetDiv {
                  ...TargetDivFields
                }
              }
            }
          }
        }
      }

      # 25. iFrame
      ... on AnchorBlockContentIframe {
        iframe {
          iframeConnection {
            totalCount
            edges {
              node {
                ... on Iframe {
                  ...IframeFields
                }
              }
            }
          }
        }
      }

      # 26. Certona Tabbed
      ... on AnchorBlockContentCertonaTabbed {
        certona_tabbed {
          certona_tabbedConnection {
            totalCount
            edges {
              node {
                ... on CertonaTabbed {
                  ...CertonaTabbledFields
                }
              }
            }
          }
        }
      }

      # 27. Deals Split
      ... on AnchorBlockContentDealsSplit {
        deals_split {
          deals_splitConnection {
            totalCount
            edges {
              node {
                ... on DealsSplit {
                  ...DealsSplitFields
                }
              }
            }
          }
        }
      }

      # 28. Pencil Banner
      ... on AnchorBlockContentPencilBanner {
        pencil_banner {
          pencil_bannerConnection {
            totalCount
            edges {
              node {
                ... on PencilBanner {
                  ...PencilBannerFields
                }
              }
            }
          }
        }
      }

      # 29. Live Text Hero
      ... on AnchorBlockContentLiveTextHero {
        live_text_hero {
          live_text_heroConnection {
            totalCount
            edges {
              node {
                ... on LiveTextHero {
                  ...LiveTextHeroFields
                }
              }
            }
          }
        }
      }
    }
    modular_action
  }
}

# =====================================================================
# FRAGMENT DEFINITIONS - BLOCKS 1-10
# =====================================================================

fragment BannerFields on Banner {
  system {
    uid
    content_type_uid
    locale
  }
  title
  heading
  heading_underline
  component_id
  asset_size
  image {
    ...ImageFields
  }
  link
  legal {
    ...LegalFields
  }
  segmentation {
    ...SegmentationFields
  }
  padding_0
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
  ymm_filter
  ymm_selector
  location_filter_enabled
  location_filter {
    ...LocationFilterFields
  }
}

fragment BannerSplitFields on BannerSplit {
  system {
    uid
    content_type_uid
    locale
  }
  title
  component_id
  headline
  layout
  asset_size
  drop_shadow
  banner_split {
    image {
      ...ImageFields
    }
    link
    segmentation {
      ...SegmentationFields
    }
    legal {
      ...LegalFields
    }
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
  ymm_filter
  ymm_selector
}

fragment ProductChartFields on ProductChart {
  system {
    uid
    content_type_uid
    locale
  }
  title
  heading
  sub_heading
  component_id
  product {
    image {
      ...ImageFields
    }
    link
    button {
      ...ButtonFields
    }
    legal {
      ...LegalFields
    }
  }
  product_table
  table_color
  legal {
    ...LegalFields
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment FaqFields on Faq {
  system {
    uid
    content_type_uid
    locale
  }
  title
  theme
  font_size
  heading
  component_id
  faq {
    question
    icon
    answer
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
  ymm_filter
  ymm_selector
  location_filter_enabled
  location_filter {
    ...LocationFilterFields
  }
}

fragment ImageContentFields on ImageContent {
  system {
    uid
    content_type_uid
    locale
  }
  title
  heading
  heading_underline
  image_direction
  component_id
  image {
    ...ImageFields
  }
  description
  mobile_number
  button_text
  button {
    ...ButtonFields
  }
  segmentation {
    ...SegmentationFields
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
  ymm_filter
  ymm_selector
}

fragment ParttypeGridFields on ParttypeGrid {
  system {
    uid
    content_type_uid
    locale
  }
  title
  headline
  underline
  layout
  grid_lines
  mobile_horizontal_scroll_disabled
  component_id
  grid_content {
    image {
      ...ImageFields
    }
    link
    label
    segmentation {
      ...SegmentationFields
    }
  }
  link {
    title
    href
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
  ymm_filter
  ymm_selector
  location_filter_enabled
  location_filter {
    ...LocationFilterFields
  }
}

fragment CategoryGridFields on CategoryGrid {
  system {
    uid
    content_type_uid
    locale
  }
  title
  headline
  component_id
  tool_content {
    image {
      ...ImageFields
    }
    link {
      title
      href
    }
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment LogoDescriptionFields on LogoDescription {
  system {
    uid
    content_type_uid
    locale
  }
  title
  component_id
  headline
  image {
    ...ImageFields
  }
  description
  button {
    ...ButtonFields
  }
  alignment
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment ImageContentBackgroundFields on ImageContentBackground {
  system {
    uid
    content_type_uid
    locale
  }
  title
  heading
  background_color
  image_direction
  component_id
  image {
    ...ImageFields
  }
  description
  button {
    ...ButtonFields
  }
  segmentation {
    ...SegmentationFields
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment ImageCategoryGridFields on ImageCategoryGrid {
  system {
    uid
    content_type_uid
    locale
  }
  title
  headline
  component_id
  image_direction
  image {
    ...ImageFields
  }
  link
  tool_content {
    image {
      ...ImageFields
    }
    link {
      title
      href
    }
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

# =====================================================================
# FRAGMENT DEFINITIONS - BLOCKS 11-20
# =====================================================================

fragment MenuListFields on MenuList {
  system {
    uid
    content_type_uid
    locale
  }
  title
  component_id
  menu_link {
    title
    href
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment BannerCtaFields on BannerCta {
  system {
    uid
    content_type_uid
    locale
  }
  title
  component_id
  image {
    ...ImageFields
  }
  main_image_position
  button {
    ...ButtonFields
  }
  segmentation {
    ...SegmentationFields
  }
  legal {
    ...LegalFields
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment ProductGridFields on ProductGrid {
  system {
    uid
    content_type_uid
    locale
  }
  title
  headline
  component_id
  headline_emphasized
  layout
  mobile_single_column
  mobile_horizontal_scroll_disabled
  product_card {
    image {
      ...ImageFields
    }
    link
    product_name
    product_description
    button {
      ...ButtonFields
    }
    warranty
  }
  product_details_alignment
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment RichTextEditorFields on RichTextEditor {
  system {
    uid
    content_type_uid
    locale
  }
  title
  component_id
  headline
  heading_underline
  heading_font
  heading_alignment
  description
  description_background
  alignment
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
  segmentation {
    ...SegmentationFields
  }
}

fragment BlogtypeGridFields on BlogtypeGrid {
  system {
    uid
    content_type_uid
    locale
  }
  title
  component_id
  headline
  background_color
  mobile_horizontal_scroll_disabled
  blog_content {
    image {
      ...ImageFields
    }
    link
    heading
    description
    link_cta {
      title
      href
    }
    target
  }
  legal {
    ...LegalFields
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
  ymm_filter
  ymm_selector
}

fragment FeaturedContentFields on FeaturedContent {
  system {
    uid
    content_type_uid
    locale
  }
  title
  headline
  headline_underline
  component_id
  headline_position
  layout
  content_display
  background_color
  feature_content {
    image {
      ...ImageFields
    }
    link
    heading
    description
  }
  legal {
    ...LegalFields
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
  ymm_filter
  ymm_selector
}

fragment VideoContentFields on VideoContent {
  system {
    uid
    content_type_uid
    locale
  }
  title
  component_id
  video
  video_url {
    ...VideoFields
  }
  video_direction
  heading
  heading_underline
  description
  button {
    ...ButtonFields
  }
  segmentation {
    ...SegmentationFields
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment ButtonGridFields on ButtonGrid {
  system {
    uid
    content_type_uid
    locale
  }
  title
  component_id
  button {
    ...ButtonFields
  }
  background_color
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment ImageVideoSplitFields on ImageVideoSplit {
  system {
    uid
    content_type_uid
    locale
  }
  title
  headline
  component_id
  split_content {
    image {
      ...ImageFields
    }
    image_target_link
    video {
      ...VideoFields
    }
    heading
    description
    segmentation {
      ...SegmentationFields
    }
    link_cta {
      title
      href
    }
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment IconGridFields on IconGrid {
  system {
    uid
    content_type_uid
    locale
  }
  title
  component_id
  grid_content {
    image {
      ...ImageFields
    }
    link
  }
  background_color
}

# =====================================================================
# FRAGMENT DEFINITIONS - BLOCKS 21-29
# =====================================================================

fragment SuperDuperHeroFields on SuperDuperHero {
  title
  hero_banner
  auto_rotation_speed
  component_id
  carousel_content {
    image {
      ...ImageFields
    }
    link
    legal {
      ...LegalFields
    }
    legal_link_color {
      ...LegalLinkColorFields
    }
    segmentation {
      ...SegmentationFields
    }
  }
  carousel_content_1 {
    __typename
  }
  taxonomy
  taxonomy_selector
}

fragment CertonaFields on Certona {
  title
  theme
  container_id
  subtitle
  component_id
  container_style {
    default_container_style
    device_specific_overrides {
      device
      container_style
    }
  }
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment ExperienceInjectorFields on ExperienceInjector {
  title
  experience_key
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
}

fragment TargetDivFields on TargetDiv {
  title
  attribute_id
  taxonomy
  taxonomy_selector
}

fragment IframeFields on Iframe {
  system {
    uid
    content_type_uid
    locale
  }
}

fragment CertonaTabbledFields on CertonaTabbed {
  title
  theme
  tabs {
    internal_title
    container_id
    subtitle
    taxonomy
    taxonomy_selector
    brand_filter
    brand_selector
  }
  subtitle
  component_id
  container_style {
    default_container_style
    device_specific_overrides {
      device
      container_style
    }
  }
}

fragment DealsSplitFields on DealsSplit {
  title
  heading
  underline
  layout
  background_color
  text_color
  image_alignment
  deal_tiles {
    heading
    description
    cta_link {
      title
      href
    }
    image {
      ...ImageFields
    }
    legal {
      ...LegalFields
    }
  }
  segmentation {
    ...SegmentationFields
  }
  taxonomy
  taxonomy_selector
}

fragment PencilBannerFields on PencilBanner {
  title
  header
  header_text_color
  body
  body_text_color
  layout
  text_align
  link
  background_color
  taxonomy
  taxonomy_selector
  brand_filter
  brand_selector
  segmentation {
    ...SegmentationFields
  }
  component_id
}

fragment LiveTextHeroFields on LiveTextHero {
  title
  component_id
  auto_rotation_speed
  hero {
    azmn
    placeholder_value
    image {
      ...ImageFields
    }
    text_color
    heading
    sub_heading
    link
    button {
      theme
      title
    }
    legal {
      ...LegalFields
    }
    segmentation {
      ...SegmentationFields
    }
  }
  segmentation {
    ...SegmentationFields
  }
  taxonomy_selector
  taxonomy
}

# =====================================================================
# GLOBAL FIELD FRAGMENTS
# =====================================================================

fragment ImageFields on Image {
  image_url
  image_alt_text
  attributes {
    transformations {
      transformation_type
      crop_width
      crop_height
      xfocuspoint
      yfocuspoint
    }
    device
    device_image
    alt_text
  }
}

fragment LegalFields on Legal {
  text
  link
  link_name
  aria_label
  copy
}

fragment LegalLinkColorFields on LegalLinkColor {
  default_color
  group {
    device
    color
  }
}

fragment SegmentationFields on Segmentation {
  segment
}

fragment ButtonFields on Button {
  theme
  link {
    title
    href
  }
}

fragment VideoFields on Video {
  video_url
  poster_image
  video_description
}

fragment LocationFilterFields on LocationFilter {
  states
  cities
}
`;
}
